import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InspectionHistory = ({ history }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');

  const filterOptions = [
    { value: 'all', label: 'All', icon: 'List' },
    { value: 'approved', label: 'Approved', icon: 'CheckCircle2' },
    { value: 'rejected', label: 'Rejected', icon: 'XCircle' },
    { value: 'pending', label: 'Pending', icon: 'Clock' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'var(--color-success)';
      case 'rejected':
        return 'var(--color-error)';
      case 'pending':
        return 'var(--color-warning)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return 'CheckCircle2';
      case 'rejected':
        return 'XCircle';
      case 'pending':
        return 'Clock';
      default:
        return 'Circle';
    }
  };

  const filteredHistory = selectedFilter === 'all' 
    ? history 
    : history?.filter(item => item?.status === selectedFilter);

  const statusCounts = history?.reduce((acc, item) => {
    acc[item.status] = (acc?.[item?.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="px-4 md:px-6 py-4 md:py-6 border-b border-border">
        <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4">Inspection History</h2>
        
        <div className="grid grid-cols-2 gap-2">
          {filterOptions?.map(option => (
            <button
              key={option?.value}
              onClick={() => setSelectedFilter(option?.value)}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all duration-250 ${
                selectedFilter === option?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon name={option?.icon} size={14} />
                <span>{option?.label}</span>
                {option?.value !== 'all' && statusCounts?.[option?.value] && (
                  <span className="ml-1">({statusCounts?.[option?.value]})</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {filteredHistory?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="FileSearch" size={48} color="var(--color-muted-foreground)" />
            <p className="mt-4 text-muted-foreground">No inspection records found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredHistory?.map(item => (
              <div
                key={item?.id}
                className="p-3 md:p-4 rounded-xl border border-border bg-card hover:bg-muted/30 transition-colors duration-250"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={16} 
                      color={getStatusColor(item?.status)} 
                    />
                    <span className={`text-xs font-medium uppercase tracking-wide`}
                          style={{ color: getStatusColor(item?.status) }}>
                      {item?.status}
                    </span>
                  </div>
                  <span className="data-text text-xs text-muted-foreground">
                    #{item?.batchNumber}
                  </span>
                </div>

                <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-1">
                  {item?.productName}
                </h4>

                <p className="caption text-muted-foreground line-clamp-2 mb-3">
                  {item?.checkpointType}
                </p>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={12} color="var(--color-muted-foreground)" />
                    <span className="caption text-muted-foreground">{item?.inspector}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={12} color="var(--color-muted-foreground)" />
                    <span className="caption text-muted-foreground">
                      {new Date(item.completedDate)?.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  {item?.defectCount > 0 && (
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={12} color="var(--color-warning)" />
                      <span className="caption text-warning">
                        {item?.defectCount} defect{item?.defectCount > 1 ? 's' : ''} found
                      </span>
                    </div>
                  )}
                </div>

                {item?.notes && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="caption text-muted-foreground line-clamp-2">{item?.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="px-4 md:px-6 py-4 border-t border-border">
        <Button variant="outline" fullWidth iconName="Download">
          Export History
        </Button>
      </div>
    </div>
  );
};

export default InspectionHistory;